import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineCancel } from 'react-icons/md';
import { useHistory } from "react-router-dom";
import Cookies from 'js-cookie';
import { Button } from '.';
import { teacherProfileData, userProfileData } from '../data/dummy';
import { useStateContext } from '../contexts/ContextProvider';
import * as Icons from '../data/nav';
import { userRequest } from '../utils/requestMethods';

const UserProfile = ({ user, school }) => {
  const { schoolId, setSchoolId, refreshSchool, fetchUserSchools } = useStateContext();
  const [loading, setLoading] = useState(false);
  const [showSchoolSwitcher, setShowSchoolSwitcher] = useState(false);
  const history = useHistory();

  // Determine which links to show based on user role
  const showAdminLinks = user?.role === 0 || user?.role === 2; // Admin or Burser
  const showTeacherLinks = user?.role === 1; // Teacher

  const switchSchool = async (newSchoolId) => {
    setSchoolId(newSchoolId);
    localStorage.setItem('selectedSchoolId', newSchoolId);
    // Refresh school data
    await refreshSchool();
    setShowSchoolSwitcher(false);
  };

  const logoutHandler = async () => {
    try {
      setLoading(true);
      const { data } = await userRequest.post("auth/logout");
      console.log(data);
      Cookies.remove('accessToken');
      localStorage.removeItem("tokenWithUser");
      localStorage.removeItem('selectedSchoolId');
      history.push("/signin");
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  return (
    <div className="nav-item absolute right-1 top-16 bg-white shadow-lg dark:bg-[#42464D] p-8 rounded-lg w-96">
      <div className="flex justify-between items-center">
        <p className="font-semibold text-lg dark:text-gray-200">User Profile</p>
        <Button
          icon={<MdOutlineCancel />}
          color="rgb(153, 171, 180)"
          bgHoverColor="light-gray"
          size="2xl"
          width="12"
          borderRadius="50%"
        />
      </div>
      
      <div className="flex gap-5 items-center mt-6 border-color border-b-1 pb-6">
        <div className="rounded-full p-2 border border-gray-200">
          <img
            className="rounded-full h-12 w-12"
            src={Icons.profile}
            alt="user-profile"
          />
        </div>
        <div>
          <p className="font-semibold text-xl dark:text-gray-200"> {user?.firstname} {user?.lastname} </p>
          <p className="text-gray-500 text-sm dark:text-gray-400">  {school?.school_name}</p>
          <p className="text-gray-500 text-sm font-semibold dark:text-gray-400"> {user?.email} </p>
        </div>
      </div>

      {/* School Switcher Section */}
      <div className="border-b-1 border-color pb-4 mb-4">
        <div className="flex justify-between items-center mb-3">
          <p className="font-semibold text-sm dark:text-gray-200">Current School</p>
          <button 
            onClick={() => setShowSchoolSwitcher(!showSchoolSwitcher)}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
          >
            {showSchoolSwitcher ? 'Hide' : 'Switch School'}
            <svg 
              className={`w-4 h-4 transition-transform ${showSchoolSwitcher ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        {/* Current School Display */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm dark:text-gray-200 text-blue-900 dark:text-blue-100">
                {school?.school_name}
              </p>
              <p className="text-blue-600 dark:text-blue-400 text-xs">
                {school?.school_email}
              </p>
            </div>
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          </div>
        </div>

        {/* School List */}
        {showSchoolSwitcher && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Available Schools ({user?.schools?.schools?.length || 0})
            </p>
            {user?.schools?.schools?.map((schoolItem) => (
              <button
                key={schoolItem._id}
                onClick={() => switchSchool(schoolItem._id)}
                className={`w-full text-left p-3 rounded-xl text-sm transition-all duration-200 border ${
                  schoolId === schoolItem._id
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    schoolId === schoolItem._id 
                      ? 'bg-blue-100 dark:bg-blue-800' 
                      : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <svg className={`w-4 h-4 ${
                      schoolId === schoolItem._id 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-500 dark:text-gray-400'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${
                      schoolId === schoolItem._id 
                        ? 'text-blue-900 dark:text-blue-100' 
                        : 'text-gray-900 dark:text-gray-200'
                    }`}>
                      {schoolItem.school_name}
                    </p>
                    <p className={`text-xs ${
                      schoolId === schoolItem._id 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {schoolItem.school_email}
                    </p>
                  </div>
                  {schoolId === schoolItem._id && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Add New School Option */}
      {showAdminLinks && (
        <Link to="/add-school" className="block">
          <div className="flex gap-5 border-b-1 border-color p-4 hover:bg-light-gray cursor-pointer dark:hover:bg-[#42464D] transition-colors duration-200">
            <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-800/30 dark:to-emerald-800/30 rounded-xl flex items-center justify-center border border-green-200 dark:border-green-700">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold dark:text-gray-200">Add New School</p>
              <p className="text-gray-500 text-sm dark:text-gray-400">Create or join another school</p>
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </Link>
      )}

      <div className="mt-5">
        <button 
          type="button" 
          onClick={logoutHandler} 
          className="w-full text-indigo-700 hover:text-white border border-indigo-700 hover:bg-indigo-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm px-5 py-2.5 text-center me-2 mb-2 dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:hover:bg-blue-500 dark:focus:ring-blue-800"
        >
          {loading ? "Logging out..." : "Logout"}
        </button>
      </div>
    </div>
  );
};

export default UserProfile;
